/**
 * Rosemont Printing: Get a Quote garment customizer.
 *
 * Markup: snippets/ps-garment-customizer.liquid (+ snippets/ps-mockup.liquid for the SVG mockups).
 *
 * HOW PLACEMENT WORKS
 * Every mockup is drawn in the same 400 x 440 SVG coordinate space. GARMENTS below lists, per
 * garment and per view (front/back), the print areas ("zones"). Each zone is a box:
 *   x, y  centre of the print area      w, h  max width/height the text may use
 *   size  starting font size            rot   rotation in degrees (sleeves)
 *   lines max lines the text may wrap to
 * The text is drawn into that box (shrunk to fit), and a dashed marker is drawn for every zone
 * of the visible view so customers can click a print area to pick it.
 * Left / Right follow the wearer's perspective, so "Left Chest" appears on the viewer's right.
 *
 * ADDING A GARMENT
 * 1. Add a mockup (front + back) in snippets/ps-mockup.liquid and its key to `mock_types` in
 *    snippets/ps-garment-customizer.liquid.
 * 2. Add an entry to GARMENTS (copy a similar garment and adjust the zone boxes).
 * 3. Add a keyword rule to PRODUCT_RULES so the Product type dropdown maps to it.
 */
(() => {
  const SVG_NS = 'http://www.w3.org/2000/svg';

  const PLACEMENT_LABELS = {
    full_front: 'Full Front',
    left_chest: 'Left Chest',
    right_chest: 'Right Chest',
    full_back: 'Full Back',
    upper_back: 'Upper Back',
    left_sleeve: 'Left Sleeve',
    right_sleeve: 'Right Sleeve',
  };

  /** Matching checkbox in the "Print locations" group for each placement. */
  const PRINT_LOCATION_FOR = {
    full_front: 'Front',
    left_chest: 'Front',
    right_chest: 'Front',
    full_back: 'Back',
    upper_back: 'Back',
    left_sleeve: 'Left Sleeve',
    right_sleeve: 'Right Sleeve',
  };

  const TEE_BACK = {
    full_back: { x: 200, y: 200, w: 160, h: 130, size: 38, lines: 2 },
    upper_back: { x: 200, y: 100, w: 150, h: 34, size: 20, lines: 1 },
  };

  const GARMENTS = {
    tee: {
      label: 'T-shirt',
      apparel: true,
      front: {
        full_front: { x: 200, y: 205, w: 150, h: 120, size: 36, lines: 2 },
        left_chest: { x: 252, y: 132, w: 56, h: 30, size: 14, lines: 2 },
        right_chest: { x: 148, y: 132, w: 56, h: 30, size: 14, lines: 2 },
        left_sleeve: { x: 324, y: 124, w: 38, h: 20, size: 11, lines: 1, rot: -54 },
        right_sleeve: { x: 76, y: 124, w: 38, h: 20, size: 11, lines: 1, rot: 54 },
      },
      back: TEE_BACK,
    },
    longsleeve: {
      label: 'Long sleeve',
      apparel: true,
      front: {
        full_front: { x: 200, y: 205, w: 150, h: 120, size: 36, lines: 2 },
        left_chest: { x: 252, y: 132, w: 56, h: 30, size: 14, lines: 2 },
        right_chest: { x: 148, y: 132, w: 56, h: 30, size: 14, lines: 2 },
        left_sleeve: { x: 340, y: 268, w: 120, h: 22, size: 15, lines: 1, rot: 82 },
        right_sleeve: { x: 60, y: 268, w: 120, h: 22, size: 15, lines: 1, rot: 98 },
      },
      back: TEE_BACK,
    },
    tank: {
      label: 'Tank top',
      apparel: true,
      front: {
        full_front: { x: 200, y: 245, w: 140, h: 110, size: 34, lines: 2 },
        left_chest: { x: 238, y: 168, w: 46, h: 26, size: 12, lines: 2 },
        right_chest: { x: 162, y: 168, w: 46, h: 26, size: 12, lines: 2 },
      },
      back: {
        full_back: { x: 200, y: 215, w: 150, h: 120, size: 36, lines: 2 },
        upper_back: { x: 200, y: 120, w: 120, h: 30, size: 18, lines: 1 },
      },
    },
    crew: {
      label: 'Sweatshirt',
      apparel: true,
      front: {
        full_front: { x: 200, y: 200, w: 160, h: 120, size: 36, lines: 2 },
        left_chest: { x: 252, y: 128, w: 58, h: 30, size: 14, lines: 2 },
        right_chest: { x: 148, y: 128, w: 58, h: 30, size: 14, lines: 2 },
        left_sleeve: { x: 343, y: 270, w: 120, h: 22, size: 15, lines: 1, rot: 82 },
        right_sleeve: { x: 57, y: 270, w: 120, h: 22, size: 15, lines: 1, rot: 98 },
      },
      back: {
        full_back: { x: 200, y: 205, w: 170, h: 130, size: 38, lines: 2 },
        upper_back: { x: 200, y: 102, w: 150, h: 34, size: 20, lines: 1 },
      },
    },
    hoodie: {
      label: 'Hoodie',
      apparel: true,
      front: {
        full_front: { x: 200, y: 222, w: 140, h: 100, size: 34, lines: 2 },
        left_chest: { x: 250, y: 150, w: 50, h: 28, size: 13, lines: 2 },
        right_chest: { x: 150, y: 150, w: 50, h: 28, size: 13, lines: 2 },
        left_sleeve: { x: 345, y: 272, w: 120, h: 22, size: 15, lines: 1, rot: 82 },
        right_sleeve: { x: 55, y: 272, w: 120, h: 22, size: 15, lines: 1, rot: 98 },
      },
      back: {
        full_back: { x: 200, y: 255, w: 165, h: 120, size: 36, lines: 2 },
        upper_back: { x: 200, y: 165, w: 150, h: 32, size: 20, lines: 1 },
      },
    },
    polo: {
      label: 'Polo',
      apparel: true,
      front: {
        full_front: { x: 200, y: 222, w: 140, h: 110, size: 32, lines: 2 },
        left_chest: { x: 250, y: 138, w: 52, h: 28, size: 13, lines: 2 },
        right_chest: { x: 150, y: 138, w: 52, h: 28, size: 13, lines: 2 },
        left_sleeve: { x: 324, y: 124, w: 38, h: 20, size: 11, lines: 1, rot: -54 },
        right_sleeve: { x: 76, y: 124, w: 38, h: 20, size: 11, lines: 1, rot: 54 },
      },
      back: TEE_BACK,
    },
    // Flat illustrations (no clickable zones); text is shown centred on the preview.
    jacket: { label: 'Jacket', apparel: true, flat: true },
    cap: { label: 'Hat / cap', flat: true },
    tote: { label: 'Bag', flat: true },
    mug: { label: 'Mug / cup', flat: true },
    dtf: { label: 'DTF transfer', flat: true, noColor: true },
    custom: { label: 'Custom product', flat: true },
  };

  /** First matching rule wins: order matters (e.g. "long sleeve" before "shirt"). */
  const PRODUCT_RULES = [
    [/hood/, 'hoodie'],
    [/sweat|crew/, 'crew'],
    [/long\s*sleeve|longsleeve|l\/s/, 'longsleeve'],
    [/tank|sleeveless|singlet/, 'tank'],
    [/polo|uniform/, 'polo'],
    [/jacket|workwear|coat|outerwear/, 'jacket'],
    [/shirt|tee/, 'tee'],
    [/hat|cap|beanie/, 'cap'],
    [/bag|tote|backpack/, 'tote'],
    [/mug|cup|tumbler|drink/, 'mug'],
    [/dtf|transfer|gang/, 'dtf'],
  ];

  const garmentFor = (value) => {
    const v = (value || '').toLowerCase();
    if (!v) return 'tee';
    const rule = PRODUCT_RULES.find(([pattern]) => pattern.test(v));
    return rule ? rule[1] : 'custom';
  };

  const isDark = (hex) => {
    const m = /^#?([0-9a-f]{6})$/i.exec((hex || '').trim());
    if (!m) return false;
    const n = parseInt(m[1], 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return 0.299 * r + 0.587 * g + 0.114 * b < 140;
  };

  const splitLines = (text, maxLines) => {
    if (maxLines < 2 || text.length <= 12 || !text.includes(' ')) return [text];
    const middle = text.length / 2;
    let best = -1;
    for (let i = 0; i < text.length; i++) {
      if (text[i] === ' ' && (best === -1 || Math.abs(i - middle) < Math.abs(best - middle))) best = i;
    }
    return best === -1 ? [text] : [text.slice(0, best), text.slice(best + 1)];
  };

  class GarmentCustomizer {
    constructor(root) {
      this.root = root;
      this.form = root.closest('form');
      const $ = (sel) => root.querySelector(sel);
      const $$ = (sel) => [...root.querySelectorAll(sel)];

      this.product = $('[data-garment-product]');
      this.preview = $('[data-garment-preview]');
      this.arts = $$('[data-art]');
      this.flatText = $('[data-flat-text]');
      this.viewButtons = $$('[data-view-btn]');
      this.productLabel = $('[data-garment-product-label]');
      this.colorLabel = $('[data-garment-color-label]');
      this.colorName = $('[data-garment-color-name]');
      this.colorsBox = $('[data-garment-colors]');
      this.colorInputs = $$('[name="contact[Garment color]"]');
      this.otherBox = $('[data-garment-other]');
      this.otherInput = this.otherBox && this.otherBox.querySelector('input');
      this.textInput = $('[data-custom-text]');
      this.textColorInputs = $$('[name="contact[Text color]"]');
      this.textColorName = $('[data-text-color-name]');
      this.placementInputs = $$('[data-placement]');
      this.placementNote = $('[data-placement-note]');
      this.placementCaption = $('[data-placement-caption]');
      this.placementLabel = $('[data-placement-label]');
      this.markerHint = $('[data-marker-hint]');
      this.sizesBox = $('[data-garment-sizes]');
      this.sizeInputs = $$('[data-size]');
      this.youthToggle = $('[data-youth-toggle]');
      this.youthGrid = $('[data-youth-grid]');
      this.sizesError = $('[data-sizes-error]');
      this.summary = $('[data-sizes-summary]');
      this.total = $('[data-total-qty]');
      this.qtyHint = $('[data-qty-hint]');

      this.type = 'tee';
      this.view = 'front';
      this.placement = null;
      this.garmentHex = '#ffffff';
      this.textHex = null;

      this.bind();
      this.applyUrlPreset();
      this.setProduct();
      const checkedColor = this.colorInputs.find((i) => i.checked);
      if (checkedColor) this.setGarmentColor(checkedColor);
      const checkedPlacement = this.placementInputs.find((i) => i.checked);
      if (checkedPlacement) this.setPlacement(checkedPlacement.dataset.placement, { fromInput: true });
    }

    get garment() {
      return GARMENTS[this.type] || GARMENTS.custom;
    }

    bind() {
      this.product.addEventListener('change', () => this.setProduct());
      this.viewButtons.forEach((btn) => btn.addEventListener('click', () => this.setView(btn.dataset.viewBtn)));
      this.colorInputs.forEach((input) => input.addEventListener('change', () => this.setGarmentColor(input)));
      this.textColorInputs.forEach((input) => input.addEventListener('change', () => this.setTextColor(input)));
      if (this.textInput) this.textInput.addEventListener('input', () => this.renderText());
      this.placementInputs.forEach((input) =>
        input.addEventListener('change', () => this.setPlacement(input.dataset.placement, { fromInput: true }))
      );
      this.sizeInputs.forEach((input) => input.addEventListener('input', () => this.updateTotal()));
      if (this.youthToggle) {
        this.youthToggle.addEventListener('change', () => {
          this.syncSizeAvailability();
          this.updateTotal();
        });
      }
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    applyUrlPreset() {
      const params = new URLSearchParams(window.location.search);
      const preset = params.get('product');
      if (!preset) return;
      const match = [...this.product.options].find(
        (o) => o.value && o.value.toLowerCase().startsWith(preset.toLowerCase())
      );
      if (match) this.product.value = match.value;
    }

    /* ---------- Product & view ---------- */

    setProduct() {
      this.type = garmentFor(this.product.value);
      const g = this.garment;
      this.productLabel.textContent = this.product.value
        ? this.product.options[this.product.selectedIndex].text
        : g.label;

      // Placements available for this garment.
      this.placementInputs.forEach((input) => {
        const key = input.dataset.placement;
        const available = g.flat || Boolean((g.front && g.front[key]) || (g.back && g.back[key]));
        input.disabled = !available;
        if (!available && input.checked) {
          input.checked = false;
          this.placement = null;
        }
      });
      const unavailable = this.placementInputs.filter((i) => i.disabled).map((i) => i.value);
      if (this.placementNote) {
        this.placementNote.hidden = unavailable.length === 0;
        this.placementNote.textContent = unavailable.length ? `Not available on ${g.label.toLowerCase()}s: ${unavailable.join(', ')}.` : '';
      }

      // Colors (hidden for DTF transfers).
      this.colorsBox.hidden = Boolean(g.noColor);
      this.colorInputs.forEach((input) => { input.disabled = Boolean(g.noColor); });
      this.syncOtherColor();

      // Sizes vs plain quantity.
      const wasApparel = this.apparel;
      this.apparel = Boolean(g.apparel);
      this.sizesBox.hidden = !this.apparel;
      this.syncSizeAvailability();
      this.total.readOnly = this.apparel;
      this.total.classList.toggle('is-calculated', this.apparel);
      this.qtyHint.textContent = this.apparel
        ? 'Calculated automatically from your size quantities.'
        : 'Enter the total number of pieces.';
      if (this.apparel) this.updateTotal();
      else if (wasApparel) this.sizesError.hidden = true;

      const zoneView = this.placement ? this.viewForPlacement(this.placement) : null;
      this.setView(zoneView || (g.flat ? 'front' : this.view));
    }

    viewForPlacement(key) {
      const g = this.garment;
      if (g.flat) return 'front';
      if (g.front && g.front[key]) return 'front';
      if (g.back && g.back[key]) return 'back';
      return null;
    }

    setView(view) {
      const g = this.garment;
      this.view = g.flat ? 'front' : view;
      this.arts.forEach((el) => {
        el.hidden = !(el.dataset.art === this.type && el.dataset.artView === this.view);
      });
      this.viewButtons.forEach((btn) => {
        btn.setAttribute('aria-pressed', String(btn.dataset.viewBtn === this.view));
        btn.disabled = Boolean(g.flat) && btn.dataset.viewBtn === 'back';
      });
      if (this.markerHint) this.markerHint.hidden = Boolean(g.flat);
      this.renderMarkers();
      this.renderText();
    }

    activeSvg() {
      const art = this.arts.find((el) => !el.hidden);
      return art ? art.querySelector('svg.ps-mock__svg') : null;
    }

    /* ---------- Colors ---------- */

    setGarmentColor(input) {
      const hex = input.dataset.hex;
      if (hex) {
        this.garmentHex = hex;
        this.preview.style.setProperty('--ps-garment', hex);
      }
      this.colorName.textContent = input.value;
      this.colorLabel.textContent = input.value;
      this.syncOtherColor(true);
      this.renderText();
    }

    syncOtherColor(focus = false) {
      if (!this.otherBox) return;
      const checked = this.colorInputs.find((i) => i.checked);
      const isOther = Boolean(checked && checked.hasAttribute('data-other')) && !this.garment.noColor;
      this.otherBox.hidden = !isOther;
      this.otherInput.disabled = !isOther;
      if (isOther && focus) this.otherInput.focus();
    }

    setTextColor(input) {
      this.textHex = input.dataset.hex;
      this.textColorName.textContent = input.value;
      this.renderText();
    }

    printColor() {
      if (this.textHex) return this.textHex;
      return isDark(this.garmentHex) ? '#ffffff' : '#111111';
    }

    /* ---------- Placement ---------- */

    setPlacement(key, { fromInput = false } = {}) {
      const input = this.placementInputs.find((i) => i.dataset.placement === key);
      if (!input || input.disabled) return;
      if (!fromInput) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        return; // the change handler calls back with fromInput
      }
      this.placement = key;
      if (this.placementCaption) this.placementCaption.hidden = false;
      if (this.placementLabel) this.placementLabel.textContent = PLACEMENT_LABELS[key];
      this.checkPrintLocation(key);
      const view = this.viewForPlacement(key);
      if (view && view !== this.view) this.setView(view);
      else {
        this.renderMarkers();
        this.renderText();
      }
    }

    /**
     * Tick the matching "Print locations" checkbox. A box ticked automatically is unticked again
     * when the placement changes, unless the customer has touched it themselves.
     */
    checkPrintLocation(key) {
      const label = PRINT_LOCATION_FOR[key];
      if (!label || !this.form) return;
      const box = this.form.querySelector(`input[type="checkbox"][name="contact[Print location: ${label}]"]`);
      if (this.autoLocation && this.autoLocation !== box && this.autoLocation.dataset.psAuto) {
        this.autoLocation.checked = false;
        delete this.autoLocation.dataset.psAuto;
      }
      this.autoLocation = null;
      if (!box) return;
      if (!box.checked) {
        box.checked = true;
        box.dataset.psAuto = 'true';
      }
      if (box.dataset.psAuto) this.autoLocation = box;
      if (!box.dataset.psWatched) {
        box.dataset.psWatched = 'true';
        box.addEventListener('change', () => delete box.dataset.psAuto);
      }
    }

    renderMarkers() {
      this.root.querySelectorAll('[data-marker-layer]').forEach((layer) => layer.replaceChildren());
      const g = this.garment;
      const svg = this.activeSvg();
      // Targets only appear when the snippet includes placement options ([data-placement]).
      if (g.flat || !svg || this.placementInputs.length === 0) return;
      const zones = g[this.view] || {};
      const layer = svg.querySelector('[data-marker-layer]');

      Object.entries(zones).forEach(([key, z]) => {
        const group = document.createElementNS(SVG_NS, 'g');
        group.setAttribute('class', `ps-mock__marker${key === this.placement ? ' is-selected' : ''}`);
        group.setAttribute('tabindex', '0');
        group.setAttribute('role', 'button');
        group.setAttribute('aria-label', `Place text: ${PLACEMENT_LABELS[key]}`);
        if (z.rot) group.setAttribute('transform', `rotate(${z.rot} ${z.x} ${z.y})`);

        const title = document.createElementNS(SVG_NS, 'title');
        title.textContent = PLACEMENT_LABELS[key];
        const rect = document.createElementNS(SVG_NS, 'rect');
        const pad = 4;
        rect.setAttribute('x', z.x - z.w / 2 - pad);
        rect.setAttribute('y', z.y - z.h / 2 - pad);
        rect.setAttribute('width', z.w + pad * 2);
        rect.setAttribute('height', z.h + pad * 2);
        rect.setAttribute('rx', 6);
        const dot = document.createElementNS(SVG_NS, 'circle');
        dot.setAttribute('cx', z.x);
        dot.setAttribute('cy', z.y);
        dot.setAttribute('r', 5);
        group.append(title, rect, dot);

        const choose = () => this.setPlacement(key);
        group.addEventListener('click', choose);
        group.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            choose();
          }
        });
        layer.appendChild(group);
      });
    }

    /* ---------- Text rendering ---------- */

    renderText() {
      // Custom text is optional: add an input with [data-custom-text] to the snippet to enable it.
      const text = this.textInput ? (this.textInput.value || '').trim() : '';
      const color = this.printColor();
      this.preview.style.setProperty('--ps-print', color);
      this.preview.classList.toggle('has-text', Boolean(text));
      this.root.querySelectorAll('[data-print-layer]').forEach((layer) => layer.replaceChildren());

      const g = this.garment;
      if (g.flat) {
        this.flatText.hidden = !text;
        this.flatText.textContent = text;
        return;
      }
      this.flatText.hidden = true;
      if (!text) return;

      const svg = this.activeSvg();
      const key = this.placement || (this.view === 'back' ? 'full_back' : 'full_front');
      const zone = svg && (g[this.view] || {})[key];
      if (!zone) return;

      const node = document.createElementNS(SVG_NS, 'text');
      node.setAttribute('text-anchor', 'middle');
      node.setAttribute('dominant-baseline', 'central');
      node.setAttribute('fill', color);
      // Thin outline keeps light text readable on light garments (and vice versa).
      node.setAttribute('stroke', isDark(color) ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.25)');
      node.setAttribute('stroke-width', '0.6');

      const lines = splitLines(text, zone.lines || 1);
      lines.forEach((line, i) => {
        const tspan = document.createElementNS(SVG_NS, 'tspan');
        tspan.setAttribute('x', zone.x);
        tspan.setAttribute('dy', i === 0 ? `${-(lines.length - 1) * 0.55}em` : '1.1em');
        tspan.textContent = line;
        node.appendChild(tspan);
      });
      node.setAttribute('x', zone.x);
      node.setAttribute('y', zone.y);
      node.setAttribute('font-size', zone.size);
      svg.querySelector('[data-print-layer]').appendChild(node);

      // Shrink to fit the zone box (measured before rotation).
      try {
        const box = node.getBBox();
        const scale = Math.min(1, zone.w / Math.max(box.width, 1), zone.h / Math.max(box.height, 1));
        if (scale < 1) node.setAttribute('font-size', Math.max(5, zone.size * scale).toFixed(2));
      } catch (e) {
        /* getBBox can fail if the SVG is not rendered yet */
      }
      if (zone.rot) node.setAttribute('transform', `rotate(${zone.rot} ${zone.x} ${zone.y})`);
    }

    /* ---------- Sizes ---------- */

    qty(input) {
      return Math.max(0, parseInt(input.value, 10) || 0);
    }

    activeSizes() {
      return this.sizeInputs.filter((input) => !input.disabled);
    }

    sizeTotal() {
      return this.activeSizes().reduce((sum, input) => sum + this.qty(input), 0);
    }

    updateTotal() {
      if (!this.apparel) return;
      const n = this.sizeTotal();
      this.total.value = n > 0 ? n : '';
      if (n > 0) this.sizesError.hidden = true;
    }

    syncSizeAvailability() {
      const youthOn = this.youthToggle ? this.youthToggle.checked : false;
      if (this.youthGrid) this.youthGrid.hidden = !youthOn;
      this.sizeInputs.forEach((input) => {
        const isYouth = this.youthGrid && this.youthGrid.contains(input);
        input.disabled = !this.apparel || (isYouth && !youthOn);
      });
    }

    onSubmit(event) {
      if (this.apparel) {
        const n = this.sizeTotal();
        if (n < 1) {
          event.preventDefault();
          this.sizesError.hidden = false;
          (this.activeSizes()[0] || this.total).focus();
          return;
        }
        this.total.value = n;
        this.summary.disabled = false;
        this.summary.value = this.activeSizes()
          .filter((input) => this.qty(input) > 0)
          .map((input) => `${input.dataset.size} x ${this.qty(input)}`)
          .join(', ');
        this.sizeInputs.forEach((input) => input.removeAttribute('name'));
      } else {
        this.summary.disabled = true;
      }
    }
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-ps-garment]').forEach((root) => {
      if (root.psGarment) return;
      root.psGarment = new GarmentCustomizer(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window.PsGarmentCustomizer = { GARMENTS, PRODUCT_RULES, init };
})();
